'use client'

/**
 * Budget allocation cards — per-channel groups of subchannel rows with budget
 * controls. Redesigned 2026-05-21 to reflect workspace-level Smart Spend:
 *   - No per-row Manual/Auto switch (replaced by workspace toggle on /budget)
 *   - No SuggestionGhost (removed with per-sub model)
 *   - When workspace Smart Spend is ON: budget inputs are read-only, locked
 *     rows show the lock icon, managed rows show a "Smart Spend" cue.
 *   - When OFF: original manual UX with −/+ buttons and editable input.
 *
 * Spec: ~/.openclaw/workspace/specs/smart-spend-v1-spec.md §14
 */

import { useCallback, useEffect, useRef, useState, type ChangeEvent, type CSSProperties } from 'react'
import Link from 'next/link'
import { HugeiconsIcon } from '@hugeicons/react'
import { Clock01Icon, LockedIcon, MinusSignIcon, PlusSignIcon } from '@hugeicons-pro/core-stroke-rounded'
import type { ChannelMetricsItem, SubchannelMetricsItem } from '@/app/api/connectors/catalog/metrics/route'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { getChannelTokenV3 } from '@/lib/budget/channel-tokens-v3'
import { endOfMonthISO } from '@/lib/budget/dates'
import { getChannelIconSolid, getChannelIconStroke } from '@/lib/channels/icons'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PushHistoryPanel } from '@/components/budget/PushHistoryPanel'
import { PushOptInDialog } from '@/components/budget/PushOptInDialog'
import { RaiseCeilingDialog } from '@/components/budget/RaiseCeilingDialog'

const CHANNEL_TILE_CLASS: Record<string, string> = {
  search: 'bg-[image:var(--tile-search)]',
  social: 'bg-[image:var(--tile-social)]',
  tv: 'bg-[image:var(--tile-tv)]',
  programmatic: 'bg-[image:var(--tile-programmatic)]',
  audio: 'bg-[image:var(--tile-audio)]',
  retail: 'bg-[image:var(--tile-retail)]',
}

// ── Types ───────────────────────────────────────────────────────

export interface SubchannelView {
  key: string
  channel_key: string
  name: string
  status: 'coming_soon' | 'available' | 'deprecated'
  connection: null | {
    id: string
    auth_mode: 'atrium_managed' | 'user_oauth'
    status: 'connected' | 'disconnected' | 'error' | 'pending'
    monthly_budget_cents: number
    min_budget_cents?: number
    max_budget_cents?: number | null
    budget_locked?: boolean
    smart_spend_paused_until?: string | null
    push_budget_enabled?: boolean
    push_budget_paused_reason?: string | null
    safety_ceiling_cents?: number
  }
}

export interface ChannelView {
  key: string
  name: string
  accent_hex: string
  display_order: number
  description: string | null
  subchannels: SubchannelView[]
}

// ── Channel-level card ──────────────────────────────────────────

export function ChannelAllocationCard({
  channel,
  metrics,
  subchannelMetrics,
  onReload,
  workspaceSmartSpendEnabled,
  monthlyCapCents,
  totalAllocatedCents,
  totalManagedCents,
  liveBudgetByConnection,
  onLiveBudgetChange,
}: {
  channel: ChannelView
  /** Channel-level rollup metrics (spend/ROAS/CTR). null when not yet loaded. */
  metrics?: ChannelMetricsItem | null
  /** Per-subchannel MTD metrics keyed by `subchannel_key`. */
  subchannelMetrics?: Record<string, SubchannelMetricsItem>
  onReload: () => void | Promise<void>
  workspaceSmartSpendEnabled: boolean
  /** Workspace monthly cap (cents). null = no cap configured (no clamp). */
  monthlyCapCents: number | null
  /** Server-snapshot total monthly_budget_cents across all connected/pending subs. */
  totalAllocatedCents: number
  /** Server-snapshot managed allocation. Only atrium_managed spend counts against cap. */
  totalManagedCents: number
  liveBudgetByConnection: Record<string, number>
  onLiveBudgetChange: (connectionId: string, cents: number) => void
}) {
  const connected = channel.subchannels.filter(
    s => s.connection?.status === 'connected' || s.connection?.status === 'pending',
  )
  const channelTotalCents = connected.reduce(
    (acc, s) => acc + (s.connection ? liveBudgetByConnection[s.connection.id] ?? s.connection.monthly_budget_cents : 0),
    0,
  )
  const channelMaxCents = connected.reduce(
    (acc, s) => acc + (s.connection?.monthly_budget_cents ?? 0),
    0,
  )
  const v3 = getChannelTokenV3(channel.key)
  const channelIcon = getChannelIconSolid(channel.key) ?? getChannelIconStroke(channel.key) ?? getChannelIconStroke('search')!

  return (
    <div
      className="mb-3.5 rounded-card border-[0.5px] border-line bg-surface-1 px-[22px] py-[18px]"
      style={{ ['--c' as string]: v3.hex } as CSSProperties}
    >
      {/* Channel header — .ah pattern from prototype */}
      <div className="flex items-center gap-[11px]">
        <div
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-[10px] text-lg text-white',
            CHANNEL_TILE_CLASS[channel.key] ?? 'bg-[linear-gradient(135deg,#9ca3af,#d1d5db)]',
          )}
        >
          <HugeiconsIcon
            icon={channelIcon}
            className="size-5"
            aria-hidden="true"
          />
        </div>
        <span className="text-lg font-bold tracking-[-0.01em] text-[var(--c)]">{channel.name}</span>
        <span className="ml-auto font-mono text-[19px] font-extrabold tracking-[-0.03em] tabular-nums">
          {formatCents(channelTotalCents)}
        </span>
      </div>
      <div className="my-[9px] mb-[13px] pl-[47px] font-mono text-xs text-text-muted-3">
        {connected.length} live subchannel{connected.length === 1 ? '' : 's'} · ROAS{' '}
        {metrics?.roas != null ? `${metrics.roas.toFixed(2)}×` : '—'}
      </div>

      {/* Rows */}
      {connected.length === 0 ? (
        <div className="ml-[47px] mt-2.5 flex flex-col items-center justify-center gap-2 border-t-[0.5px] border-line px-0 pb-1 pt-3.5 text-center">
          <span className="text-[13px] text-text-muted-2">
            No connected subchannels yet.{' '}
            <Link href="/marketplace" className="text-brand underline">
              Browse the marketplace
            </Link>{' '}
            to connect.
          </span>
        </div>
      ) : (
        <div className="ml-[47px] mt-2.5 flex flex-col gap-2 border-t-[0.5px] border-line pt-3">
          {connected.map(s => (
            <SubchannelBudgetRow
              key={s.key}
              sub={s}
              accent={v3.hex}
              spentCents={subchannelMetrics?.[s.key]?.spend_cents ?? 0}
              onReload={onReload}
              workspaceSmartSpendEnabled={workspaceSmartSpendEnabled}
              monthlyCapCents={monthlyCapCents}
              totalAllocatedCents={totalAllocatedCents}
              totalManagedCents={totalManagedCents}
              channelMaxCents={channelMaxCents}
              onLiveBudgetChange={onLiveBudgetChange}
            />
          ))}
          {/* Stephen 2026-05-23: "+ add channels" footer on every nonempty
              card so the user always has a path back to the marketplace. */}
          <Link
            href="/marketplace"
            className="flex items-center justify-center gap-1.5 pt-2 font-mono text-[11px] font-semibold text-text-muted-3 no-underline"
          >
            <HugeiconsIcon icon={PlusSignIcon} className="size-3.5" aria-hidden="true" />
            add channels
          </Link>
        </div>
      )}
    </div>
  )
}

// ── Subchannel row ──────────────────────────────────────────────

function SubchannelBudgetRow({
  sub,
  accent,
  spentCents,
  onReload,
  workspaceSmartSpendEnabled,
  monthlyCapCents,
  totalAllocatedCents,
  totalManagedCents,
  channelMaxCents,
  onLiveBudgetChange,
}: {
  sub: SubchannelView
  accent: string
  /** Month-to-date spend for this subchannel (cents). 0 when unknown. */
  spentCents: number
  onReload: () => void | Promise<void>
  workspaceSmartSpendEnabled: boolean
  monthlyCapCents: number | null
  totalAllocatedCents: number
  totalManagedCents: number
  channelMaxCents: number
  onLiveBudgetChange: (connectionId: string, cents: number) => void
}) {
  const conn = sub.connection
  if (!conn) return null

  const currentCents = conn.monthly_budget_cents
  const step = stepFor(currentCents)
  const locked = conn.budget_locked ?? false

  // A sub is "read-only" when workspace SS is ON and it's not user-locked.
  // User-locked rows always show the lock icon (explicit override).
  const managedBySmartSpend = workspaceSmartSpendEnabled && !locked

  return (
    <div className="py-1">
      <div className="grid grid-cols-[160px_1fr] items-center gap-4">
        {/* Name only — Stephen 2026-05-22 dropped the per-row subtitle. */}
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2 truncate text-[13px] font-semibold text-text-strong">
            <span className="truncate">{sub.name}</span>
            {conn.auth_mode === 'user_oauth' && (
              <span
                className="ml-1 inline-flex items-center gap-1 rounded-md border border-atrium-border bg-atrium-paper px-1.5 py-0.5 text-[10px] font-medium text-atrium-muted"
                title="This account is linked via OAuth — you fund the platform directly. Atrium currently shows your intended budget for planning."
              >
                Linked
              </span>
            )}
          </div>
        </div>

        {/* Slider + watermark + buttons + dollar. */}
        <BudgetAdjuster
          connectionId={conn.id}
          accent={accent}
          currentCents={currentCents}
          spentCents={spentCents}
          step={step}
          locked={locked}
          readOnly={managedBySmartSpend}
          onChange={onReload}
          monthlyCapCents={monthlyCapCents}
          totalAllocatedCents={totalAllocatedCents}
          totalManagedCents={totalManagedCents}
          pageMaxCents={Math.max(channelMaxCents, currentCents)}
          authMode={conn.auth_mode}
          pushBudgetEnabled={conn.push_budget_enabled ?? false}
          pushBudgetPausedReason={conn.push_budget_paused_reason ?? null}
          platformName={sub.name}
          onOptimisticChange={(cents) => onLiveBudgetChange(conn.id, cents)}
        />
      </div>

    </div>
  )
}

// ── Spent-watermark subtitle ────────────────────────────────────

/**
 * Line that sits under each subchannel row:
 *   "Remaining $Y of $Z · $X already spent"  (normal)
 *   "Over by $W · $X already spent of $Z"   (spent > budget)
 *
 * Always rendered — even when read-only — because the watermark info is
 * useful regardless of who's driving allocation.
 */
function SpentWatermarkSubtitle({
  budgetCents,
  spentCents,
  headroomCents,
  authMode,
  pushBudgetEnabled,
  pushBudgetPausedReason,
  platformName,
}: {
  budgetCents: number
  spentCents: number
  headroomCents: number | null
  authMode: 'atrium_managed' | 'user_oauth'
  pushBudgetEnabled: boolean
  pushBudgetPausedReason: string | null
  platformName: string
}) {
  const linkedSuffix = authMode === 'user_oauth'
    ? pushBudgetEnabled && pushBudgetPausedReason === null
      ? ` · live on ${platformName}`
      : pushBudgetPausedReason
        ? ' · push paused'
        : ' · target (not pushed)'
    : ''
  const overspent = spentCents > budgetCents
  if (overspent) {
    const overBy = spentCents - budgetCents
    return (
      <div
        className="mt-1 pl-44 font-mono text-[11px] text-text-muted-3"
      >
        <span className="font-semibold text-red-600">
          Over by {formatCents(overBy)}
        </span>
        {' · '}
        {formatCents(spentCents)} already spent of {formatCents(budgetCents)}
        {linkedSuffix}
      </div>
    )
  }
  if (headroomCents != null && budgetCents > headroomCents) {
    const overBy = budgetCents - headroomCents
    return (
      <div className="mt-1 text-[11px] text-atrium-muted">
        <span className="font-medium text-red-600">
          Over your monthly limit by {formatCents(overBy)}
        </span>
        {' · '}
        {formatCents(spentCents)} already spent of {formatCents(budgetCents)}
        {linkedSuffix}
      </div>
    )
  }
  const remaining = Math.max(0, budgetCents - spentCents)
  return (
    <div
      className="mt-1 pl-44 font-mono text-[11px] text-text-muted-3"
    >
      Remaining {formatCents(remaining)} of {formatCents(budgetCents)} · {formatCents(spentCents)} already spent
      {linkedSuffix}
    </div>
  )
}

// ── Dollar + adjuster ──────────────────────────────────────────

function BudgetAdjuster({
  connectionId,
  accent,
  currentCents,
  spentCents,
  step,
  locked,
  readOnly,
  onChange,
  monthlyCapCents,
  totalAllocatedCents,
  totalManagedCents,
  pageMaxCents,
  authMode,
  pushBudgetEnabled,
  pushBudgetPausedReason,
  platformName,
  onOptimisticChange,
}: {
  connectionId: string
  /** Channel accent color (hex) — used for the slider fill. */
  accent: string
  currentCents: number
  /** Month-to-date spend (cents). Used for the soft-floor confirmation. */
  spentCents: number
  step: number
  locked: boolean
  /** True when workspace Smart Spend is managing this row. */
  readOnly: boolean
  onChange: () => void | Promise<void>
  /** Workspace monthly cap (cents). null = no cap configured (no clamp). */
  monthlyCapCents: number | null
  /** Server-snapshot total allocation across all subs. */
  totalAllocatedCents: number
  /** Server-snapshot atrium_managed allocation across all subs. */
  totalManagedCents: number
  /** Largest monthly_budget_cents across visible subs. Used as the slider
   *  ceiling when there's no workspace cap. */
  pageMaxCents: number
  authMode: 'atrium_managed' | 'user_oauth'
  pushBudgetEnabled: boolean
  pushBudgetPausedReason: string | null
  platformName: string
  onOptimisticChange: (cents: number) => void
}) {
  /**
   * Optimistic local value — updated immediately on +/- click.
   * PATCH fires debounced in the background. On error: reverts + toast.
   * Buttons never disable (rapid clicks accumulate locally).
   */
  const [optimisticCents, setOptimisticCents] = useState(currentCents)
  const [editing, setEditing] = useState(false)
  const [draftDollars, setDraftDollars] = useState<string>((currentCents / 100).toString())
  const [optInOpen, setOptInOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [ceilingExceededState, setCeilingExceededState] = useState<{
    currentCeilingCents: number
    requestedCents: number
    priorCents: number
    extra?: Record<string, unknown>
  } | null>(null)

  const setOptimisticBudget = useCallback((nextCents: number) => {
    setOptimisticCents(nextCents)
    onOptimisticChange(nextCents)
  }, [onOptimisticChange])

  // Keep optimistic value in sync when server data changes (e.g. after reload)
  const prevServerCents = useRef(currentCents)
  useEffect(() => {
    if (prevServerCents.current !== currentCents && !editing) {
      prevServerCents.current = currentCents
      setOptimisticBudget(currentCents)
    }
  }, [currentCents, editing, setOptimisticBudget])

  // Compute the maximum value this row can take without breaching the
  // workspace monthly cap. The server-snapshot total reflects the *committed*
  // allocation across all subs; subtracting this row's server-committed value
  // leaves "everyone else's allocation", and the headroom is `cap - other`.
  //
  // headroom === null when there's no cap configured (no clamp).
  // headroom can be 0 if the workspace is already over cap. We still allow
  // users to set any value and show the over-cap portion visually.
  const headroomCents: number | null = monthlyCapCents == null
    || authMode !== 'atrium_managed'
    ? null
    : Math.max(0, monthlyCapCents - (totalManagedCents - currentCents))

  // Debounce PATCH — coalesce rapid clicks into one network request
  const pendingPatch = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handlePatchFailure = useCallback((
    res: Response,
    data: { error?: string; code?: string; current_ceiling_cents?: number; requested_cents?: number },
    nextCents: number,
    priorCents: number,
    extra?: Record<string, unknown>,
  ) => {
    if (res.status === 409 && data.code === 'ceiling_exceeded') {
      setCeilingExceededState({
        currentCeilingCents: Number(data.current_ceiling_cents ?? 0),
        requestedCents: Number(data.requested_cents ?? nextCents),
        priorCents,
        extra,
      })
      return
    }

    setOptimisticBudget(priorCents)
    setDraftDollars((priorCents / 100).toString())
    if (data.code === 'exceeds_monthly_cap') {
      toast.error(data.error || 'Total allocation would exceed your monthly limit.')
    } else {
      toast.error(data.error || 'Budget update failed — reverted.')
    }
  }, [setOptimisticBudget])

  const schedulePatch = useCallback((nextCents: number, extra?: Record<string, unknown>, priorCents = currentCents) => {
    if (pendingPatch.current) clearTimeout(pendingPatch.current)
    pendingPatch.current = setTimeout(() => {
      pendingPatch.current = null
      void (async () => {
        const res = await fetch(`/api/connectors/connections/${connectionId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ monthly_budget_cents: nextCents, ...(extra ?? {}) }),
        })
        if (res.ok) {
          await onChange()
        } else {
          const data = await res.json().catch(() => ({})) as {
            error?: string
            code?: string
            current_ceiling_cents?: number
            requested_cents?: number
          }
          handlePatchFailure(res, data, nextCents, priorCents, extra)
        }
      })()
    }, 300)
  }, [connectionId, currentCents, handlePatchFailure, onChange])

  const retryBudgetPatch = async (newCeilingCents: number) => {
    if (!ceilingExceededState) return
    const ceilingRes = await fetch(`/api/connectors/connections/${connectionId}/ceiling`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ new_ceiling_cents: newCeilingCents }),
    })
    const ceilingData = await ceilingRes.json().catch(() => ({})) as { error?: string }
    if (!ceilingRes.ok || ceilingData.error) {
      toast.error(ceilingData.error || 'Failed to raise safety ceiling.')
      return
    }

    const patchRes = await fetch(`/api/connectors/connections/${connectionId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        monthly_budget_cents: ceilingExceededState.requestedCents,
        ...(ceilingExceededState.extra ?? {}),
      }),
    })
    const patchData = await patchRes.json().catch(() => ({})) as { error?: string }
    if (!patchRes.ok || patchData.error) {
      toast.error(patchData.error || 'Budget update failed after raising ceiling.')
      return
    }

    setCeilingExceededState(null)
    await onChange()
  }

  const cancelCeilingRaise = () => {
    if (ceilingExceededState) {
      setOptimisticBudget(ceilingExceededState.priorCents)
      setDraftDollars((ceilingExceededState.priorCents / 100).toString())
    }
    setCeilingExceededState(null)
  }

  // Soft-floor confirmation state. When user attempts to set the budget below
  // already-spent, we open a Dialog asking them to confirm stopping the
  // channel for the rest of the month. Cancel → revert; confirm → commit +
  // pause until end of month.
  const [pendingFloor, setPendingFloor] = useState<{ cents: number; priorCents: number } | null>(null)

  const cancelFloor = () => {
    if (pendingFloor) {
      setOptimisticBudget(pendingFloor.priorCents)
      setDraftDollars((pendingFloor.priorCents / 100).toString())
    }
    setPendingFloor(null)
  }

  const confirmFloor = () => {
    if (!pendingFloor) return
    const target = pendingFloor.cents
    setPendingFloor(null)
    setOptimisticBudget(target)
    setDraftDollars((target / 100).toString())
    schedulePatch(target, { smart_spend_paused_until: endOfMonthISO() }, pendingFloor.priorCents)
  }

  const commitFromInput = async () => {
    const parsed = parseFloat(draftDollars)
    if (Number.isNaN(parsed) || parsed < 0) {
      setDraftDollars((optimisticCents / 100).toString())
      setEditing(false)
      return
    }
    const cents = Math.round(parsed * 100)

    setEditing(false)
    setDraftDollars((cents / 100).toString())
    if (cents === optimisticCents) return

    // Soft floor: setting budget below MTD spend (but above zero) requires
    // confirmation. Zero is allowed without confirm — that's an explicit
    // "stop entirely" action and the existing flow already handles it.
    if (cents > 0 && spentCents > 0 && cents < spentCents) {
      setPendingFloor({ cents, priorCents: optimisticCents })
      return
    }

    setOptimisticBudget(cents)
    schedulePatch(cents, undefined, optimisticCents)
  }

  const dec = () => {
    if (locked || readOnly) return
    const next = Math.max(0, optimisticCents - step)
    if (next > 0 && spentCents > 0 && next < spentCents) {
      setPendingFloor({ cents: next, priorCents: optimisticCents })
      return
    }
    setOptimisticBudget(next)
    schedulePatch(next, undefined, optimisticCents)
  }

  const inc = () => {
    if (locked || readOnly) return
    const candidate = Math.min(sliderMax, optimisticCents + step)
    setOptimisticBudget(candidate)
    schedulePatch(candidate, undefined, optimisticCents)
  }

  // Stephen 2026-05-22: hold-to-repeat increment. After the initial click,
  // pressing-and-holding fires the same step repeatedly. Starts slow (350ms)
  // and accelerates to fast (60ms) after a brief warm-up so users can fine-tune
  // *or* sprint.
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const stopHolding = useCallback(() => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current)
      holdTimer.current = null
    }
  }, [])
  const startHolding = useCallback(
    (action: () => void) => {
      stopHolding()
      // First repeat after 350ms; each subsequent tick faster down to 60ms.
      let delay = 350
      const tick = () => {
        action()
        delay = Math.max(60, Math.round(delay * 0.78))
        holdTimer.current = setTimeout(tick, delay)
      }
      holdTimer.current = setTimeout(tick, delay)
    },
    [stopHolding],
  )
  // Always clear on unmount/blur/etc.
  useEffect(() => stopHolding, [stopHolding])

  // ── Slider scale ─────────────────────────────────────────
  //
  // Keep the slider bounded to the channel/page budget instead of adding
  // synthetic headroom beyond the visible allocation.
  const sliderMax = Math.max(pageMaxCents, optimisticCents, 0)
  const spentPct = sliderMax > 0 ? Math.min(100, (spentCents / sliderMax) * 100) : 0
  const budgetPct = sliderMax > 0 ? Math.min(100, (optimisticCents / sliderMax) * 100) : 0
  const headroomPct = headroomCents == null || sliderMax <= 0
    ? null
    : Math.min(100, (headroomCents / sliderMax) * 100)
  const overCap = headroomCents != null && optimisticCents > headroomCents
  const accentFillPct = overCap && headroomPct != null
    ? Math.min(budgetPct, headroomPct)
    : budgetPct
  const overflowLeftPct = overCap && headroomPct != null ? headroomPct : 0
  const overflowWidthPct = overCap && headroomPct != null
    ? Math.max(0, budgetPct - headroomPct)
    : 0
  // Slider step in dollars (HTML range works in plain numbers, not cents).
  const sliderStepDollars = Math.max(1, Math.round(step / 100))

  const handleSliderChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (locked || readOnly) return
    const cents = Math.round(Number(e.target.value) * 100)

    // Soft floor: if dragging below MTD spend, hold the dialog open and
    // optimistically pin the slider at the user's draft. We do NOT patch
    // until they confirm; the optimistic value reverts if they cancel.
    if (cents > 0 && spentCents > 0 && cents < spentCents) {
      setOptimisticBudget(cents)
      // Only open the dialog once per drag; subsequent changes update target.
      setPendingFloor((prev) => ({
        cents,
        priorCents: prev?.priorCents ?? currentCents,
      }))
      return
    }

    setOptimisticBudget(cents)
    schedulePatch(cents, undefined, optimisticCents)
  }

  const pushState = authMode === 'user_oauth'
    ? pushBudgetEnabled && pushBudgetPausedReason === null
      ? 'live'
      : pushBudgetPausedReason
        ? 'paused'
        : 'off'
    : 'off'

  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-3">
      {/* Slider with spent-watermark overlay. Hidden when read-only (Smart
          Spend manages this row). Subtitle still renders below. */}
      {!readOnly && (
        <div className="relative h-5 min-w-[140px] flex-1">
          <svg
            className="absolute inset-x-0 top-1/2 h-2 -translate-y-1/2 overflow-hidden rounded-full"
            viewBox="0 0 100 8"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <rect className="fill-atrium-content" width="100" height="8" rx="4" />
            <rect className="fill-atrium-border-dark" width={spentPct} height="8" rx="4" />
            <rect className="fill-[var(--c)] opacity-55" width={accentFillPct} height="8" rx="4" />
            {overCap && (
              <rect
                className="fill-red-500 opacity-55"
                x={overflowLeftPct}
                width={overflowWidthPct}
                height="8"
                rx="4"
              />
            )}
          </svg>
          {/* Watermark vertical line at spent boundary */}
          {spentCents > 0 && (
            <svg
              className="pointer-events-none absolute inset-0 h-5 w-full overflow-visible"
              viewBox="0 0 100 20"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <title>{`Already spent: ${formatCents(spentCents)}`}</title>
              <line
                className="stroke-atrium-black"
                x1={spentPct}
                x2={spentPct}
                y1="0"
                y2="20"
                strokeWidth="0.6"
              />
            </svg>
          )}
          {/* Native range input — styled so the track is invisible (we draw it
              with the divs above) and only the thumb is visible. Thumb is
              tinted with the channel's v3 hex via the --thumb CSS var. */}
          <input
            type="range"
            min={0}
            max={Math.round(sliderMax / 100)}
            step={sliderStepDollars}
            value={Math.round(optimisticCents / 100)}
            onChange={handleSliderChange}
            disabled={locked}
            style={{ ['--thumb' as string]: accent } as CSSProperties}
            aria-label={`Budget for this channel. Already spent ${formatCents(spentCents)}.`}
            className={cn(
              'absolute inset-0 w-full h-5 cursor-pointer appearance-none bg-transparent',
              // Webkit thumb
              '[&::-webkit-slider-thumb]:appearance-none',
              '[&::-webkit-slider-thumb]:size-4',
              '[&::-webkit-slider-thumb]:rounded-full',
              '[&::-webkit-slider-thumb]:bg-[var(--thumb)]',
              '[&::-webkit-slider-thumb]:border-2',
              '[&::-webkit-slider-thumb]:border-white',
              '[&::-webkit-slider-thumb]:shadow-md',
              '[&::-webkit-slider-thumb]:cursor-pointer',
              // Firefox thumb
              '[&::-moz-range-thumb]:size-4',
              '[&::-moz-range-thumb]:rounded-full',
              '[&::-moz-range-thumb]:bg-[var(--thumb)]',
              '[&::-moz-range-thumb]:border-2',
              '[&::-moz-range-thumb]:border-white',
              '[&::-moz-range-thumb]:cursor-pointer',
              // Invisible track on both engines (we draw it via divs above)
              '[&::-webkit-slider-runnable-track]:bg-transparent',
              '[&::-moz-range-track]:bg-transparent',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'focus:outline-none focus-visible:[&::-webkit-slider-thumb]:ring-2',
              'focus-visible:[&::-webkit-slider-thumb]:ring-accent-500/30',
            )}
          />
        </div>
      )}

      {/* −/+ buttons — hidden when read-only */}
      {!readOnly && (
        <div className="flex w-[60px] shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={dec}
            onPointerDown={() => startHolding(dec)}
            onPointerUp={stopHolding}
            onPointerLeave={stopHolding}
            onPointerCancel={stopHolding}
            disabled={locked || optimisticCents <= 0}
            className={cn(
              'inline-flex size-7 items-center justify-center rounded-md border border-atrium-border bg-white text-atrium-black transition-colors',
              'hover:bg-atrium-content focus:outline-none focus:ring-2 focus:ring-accent-500/30',
              'disabled:opacity-40 disabled:cursor-not-allowed',
            )}
            aria-label="Decrease budget"
          >
            <HugeiconsIcon icon={MinusSignIcon} className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={inc}
            onPointerDown={() => startHolding(inc)}
            onPointerUp={stopHolding}
            onPointerLeave={stopHolding}
            onPointerCancel={stopHolding}
            disabled={locked}
            className={cn(
              'inline-flex size-7 items-center justify-center rounded-md border border-atrium-border bg-white text-atrium-black transition-colors',
              'hover:bg-atrium-content focus:outline-none focus:ring-2 focus:ring-accent-500/30',
              'disabled:opacity-40 disabled:cursor-not-allowed',
            )}
            aria-label="Increase budget"
          >
            <HugeiconsIcon icon={PlusSignIcon} className="size-3.5" />
          </button>
        </div>
      )}

      {/* Dollar value */}
      <div className="w-24 text-right">
        {!readOnly && editing ? (
          <input
            type="number"
            min={0}
            step={Math.max(1, step / 100)}
            value={draftDollars}
            onChange={e => setDraftDollars(e.target.value)}
            onBlur={commitFromInput}
            onKeyDown={e => {
              if (e.key === 'Enter') void commitFromInput()
              else if (e.key === 'Escape') {
                setDraftDollars((optimisticCents / 100).toString())
                setEditing(false)
              }
            }}
            autoFocus
            className="w-full rounded-md border border-accent-500 bg-white px-2 py-1 text-right font-mono text-sm focus:outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              if (readOnly) return
              setDraftDollars((optimisticCents / 100).toString())
              setEditing(true)
            }}
            disabled={locked || readOnly}
            className={cn(
              'w-full rounded-md px-1 py-0.5 text-right font-mono text-sm text-atrium-black transition-colors',
              readOnly
                ? 'cursor-default opacity-70'
                : 'hover:bg-atrium-content disabled:cursor-not-allowed disabled:opacity-60',
            )}
            title={
              locked
                ? 'Locked — unlock to edit'
                : readOnly
                  ? 'Smart Spend is managing this allocation'
                  : 'Click to type a custom amount'
            }
          >
            {locked && (
              <HugeiconsIcon icon={LockedIcon} className="mr-1 inline-block size-3" />
            )}
            {formatCents(optimisticCents)}
          </button>
        )}
      </div>
      {authMode === 'user_oauth' && (
        <div className="flex shrink-0 items-center gap-1">
          {pushState === 'off' ? (
            <>
              <span className="inline-flex max-w-[120px] items-center rounded-md border border-atrium-border bg-atrium-paper px-1.5 py-0.5 text-[10px] font-medium text-atrium-muted">
                Push off
              </span>
              <button
                type="button"
                onClick={() => setOptInOpen(true)}
                className="rounded-md border border-atrium-border bg-white px-2 py-1 text-[11px] font-medium text-atrium-black transition-colors hover:bg-atrium-content"
              >
                Enable push
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => pushState === 'paused' ? setOptInOpen(true) : setHistoryOpen(true)}
              className={cn(
                'inline-flex max-w-[120px] items-center truncate rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-colors',
                pushState === 'live'
                  ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                  : 'border-yellow-200 bg-yellow-50 text-yellow-800 hover:bg-yellow-100',
              )}
              title={
                pushState === 'live'
                  ? `Live on ${platformName}`
                  : `Push paused — ${pushBudgetPausedReason}`
              }
            >
              <span className="truncate">
                {pushState === 'live' ? `Live on ${platformName}` : `Push paused — ${pushBudgetPausedReason}`}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="inline-flex size-7 items-center justify-center rounded-md border border-atrium-border bg-white text-atrium-black transition-colors hover:bg-atrium-content focus:outline-none focus:ring-2 focus:ring-accent-500/30"
            aria-label="Open push history"
            title="Push history"
          >
            <HugeiconsIcon icon={Clock01Icon} className="size-3.5" />
          </button>
        </div>
      )}
      </div>

      <Dialog
        open={pendingFloor !== null}
        onOpenChange={(open) => {
          if (!open) cancelFloor()
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Stop spending on this channel?</DialogTitle>
            <DialogDescription>
              You&apos;ve already spent{' '}
              <strong className="text-atrium-black">{formatCents(spentCents)}</strong> this
              month. Lowering the budget to{' '}
              <strong className="text-atrium-black">
                {pendingFloor ? formatCents(pendingFloor.cents) : ''}
              </strong>{' '}
              stops this channel for the rest of the month.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={cancelFloor}
              className="rounded-lg border border-atrium-border bg-white px-4 py-2 text-sm text-atrium-black hover:bg-atrium-content transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmFloor}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
            >
              Stop channel
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SpentWatermarkSubtitle
        budgetCents={optimisticCents}
        spentCents={spentCents}
        headroomCents={headroomCents}
        authMode={authMode}
        pushBudgetEnabled={pushBudgetEnabled}
        pushBudgetPausedReason={pushBudgetPausedReason}
        platformName={platformName}
      />
      {authMode === 'user_oauth' && (
        <>
          <PushOptInDialog
            open={optInOpen}
            onOpenChange={setOptInOpen}
            connectionId={connectionId}
            platformName={platformName}
            onSuccess={async () => {
              await onChange()
              setOptInOpen(false)
            }}
          />
          <RaiseCeilingDialog
            open={ceilingExceededState !== null}
            onCancel={cancelCeilingRaise}
            onConfirm={retryBudgetPatch}
            currentCeilingCents={ceilingExceededState?.currentCeilingCents ?? 0}
            requestedCents={ceilingExceededState?.requestedCents ?? 0}
            platformName={platformName}
          />
          <PushHistoryPanel
            open={historyOpen}
            onOpenChange={setHistoryOpen}
            connectionId={connectionId}
            platformName={platformName}
            onDisabled={onChange}
          />
        </>
      )}
    </div>
  )
}

// ── Helpers ─────────────────────────────────────────────────────

function formatCents(cents: number): string {
  if (cents === 0) return '$0'
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

/**
 * Pick a "nice" increment step for the current budget magnitude.
 *   < $50      → $5
 *   < $500     → $25
 *   < $5,000   → $100
 *   < $50,000  → $500
 *   else       → $1,000
 */
function stepFor(currentCents: number): number {
  const amount = currentCents / 100
  if (amount < 50) return 5_00
  if (amount < 500) return 25_00
  if (amount < 5_000) return 100_00
  if (amount < 50_000) return 500_00
  return 1_000_00
}
